import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import { Button, Chip, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import type { CompanyModuleStatusDto } from '@/shared/api/companies';
import { getRoutePath } from '@/shared/utils/routes';

import { useAiKnowledgeBaseEntries } from '../model/useAiKnowledgeBase';

export function KnowledgeBaseModuleSummary({
  companyId,
  status,
  accentColor,
  disabled = false,
}: {
  companyId: string;
  status: CompanyModuleStatusDto;
  accentColor: string;
  disabled?: boolean;
}) {
  const { t } = useTranslation('company');
  const navigate = useNavigate();
  const entriesQuery = useAiKnowledgeBaseEntries(companyId, { limit: 1 }, !disabled);
  const total = entriesQuery.data?.total ?? 0;
  const activeCount = entriesQuery.data?.active_count ?? 0;
  const isEnabled = status === 'enabled';

  return (
    <Stack spacing={1.1}>
      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
        <Chip
          size="small"
          label={
            isEnabled
              ? t('modules.knowledgeBase.moduleEnabled')
              : t('modules.knowledgeBase.moduleDisabled')
          }
          sx={{
            borderRadius: 1,
            bgcolor: alpha(accentColor, isEnabled ? 0.1 : 0.06),
            color: isEnabled ? accentColor : 'text.secondary',
            border: '1px solid',
            borderColor: alpha(accentColor, isEnabled ? 0.22 : 0.12),
          }}
        />
        <Chip
          size="small"
          label={t('modules.knowledgeBase.count', { count: total })}
          sx={{
            borderRadius: 1,
            color: 'text.secondary',
          }}
        />
        <Chip
          size="small"
          label={t('modules.knowledgeBase.activeCount', { count: activeCount })}
          sx={{
            borderRadius: 1,
            color: 'text.secondary',
          }}
        />
      </Stack>

      <Typography variant="body2" color="text.secondary">
        {t('modules.knowledgeBase.summary')}
      </Typography>

      <Button
        size="small"
        variant="outlined"
        startIcon={<OpenInNewOutlinedIcon fontSize="small" />}
        disabled={disabled}
        onClick={() => {
          navigate(
            getRoutePath('MAIN', 'COMPANY_SETTINGS')
              .replace(':id', companyId)
              .replace(':tab', 'knowledge-base')
          );
        }}
        sx={{
          alignSelf: 'flex-start',
          minHeight: 34,
          borderRadius: 1,
          textTransform: 'none',
          fontWeight: 800,
        }}
      >
        {t('modules.knowledgeBase.open')}
      </Button>
    </Stack>
  );
}
